import { Button, Col, Row } from "react-bootstrap";
import { default as React, useMemo } from "react";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import "containers/common/List.css";
import "containers/common/Table.css";

import {
  createColumnHelper,
} from "@tanstack/react-table";

function OwnerPageIndexList({
  data,
  controlType,
  saveRecord,
  user,
  updateTableState,
  ownerid,
}) {

  // --- TanStack Table Setup ---
  const tableData = data || [];
  const columnHelper = createColumnHelper();

  const createId = useMemo(() => createElementIdFactory("owner-page-index-list"), []);
  const tableElementIds = useMemo(() => createReusableTableElementIds(createId), [createId]);

  const columns = [
    columnHelper.accessor("owner_index_id", {
      id: "owner_index_id",
      header: "ID",
      size: 60,
      cell: ({ getValue }) => {
        const val = getValue();
        return val && String(val).startsWith('temp-') ? '' : val;
      },
    }),
    columnHelper.accessor("owner_name", {
      id: "owner_name",
      header: "Name",
      cell: EditableCell,
      meta: { required: true }
    }),
    columnHelper.accessor("owner_index_id", {
      id: "delete_owner_index_id",
      header: "",
      enableSorting: false,
      cell: ({ row, table }) => (
        <Button type="button" size="sm" variant="danger" onClick={() => table.options.meta.handleDelete({ row })} >
          Delete
        </Button>
      ),
      size: 65,
    }),
  ];

  // --- Data Fetching and Update Functions ---
  async function addRecord() {
    const newEntry = {
      owner_index_id: `temp-${Date.now()}`,
      owner_id: ownerid,
      owner_name: "",
      created_by: user,
      last_edited_by: user,
      new_record: true
    };

    const updatedData = [newEntry, ...(data || [])];
    updateTableState(updatedData, 'index');
  }

  // --- Render Functions ---
  function renderList(data) {
    const visibleRows = (data || []).filter(d => Boolean((d?.owner_name || "").trim()));

    if (visibleRows.length === 0) {
      return <div className="p-3 text-muted">No owners found.</div>;
    }

    return (
      <table className="table table-borderless table-sm mt-1">
        <tbody>
          {visibleRows.map((d, i) => (
            <tr key={d.owner_index_id || `${d.owner_name || 'owner-index'}-${i}`} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td className="py-2">
                <span style={{ color: '#333', fontSize: '0.8rem', display: 'block' }}>
                  {d.owner_name}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  function renderComponent() {
    return (
      <div className="componentRendering">
        {controlType === "table" && <Row className="show-Container">
          <Col className="text-right mb-2" >
            <Button id={createId("add-btn")} variant="secondary" size="sm" onClick={() => addRecord()}>Add</Button>
          </Col>
        </Row>}

        <Row className="show-Container">
          <Col sm={12} md={12} lg={12}>
            {
              controlType === "table"
                ? <GenericTable
                    columns={columns}
                    data={tableData}
                    section="owner-index-list"
                    elementIds={tableElementIds}
                    pageSize={PAGE_SIZES.ownerDetailIndexModalTable}
                    enablePagination={true}
                    meta={{
                      updateData: (rowIndex, columnId, value) => {
                        updateTableState(rowIndex, columnId, value);
                      },
                      handleSave: async ({ row, value, column }) => {
                        const recordToSave = {
                          ...row.original,
                          [column.id]: value
                        };
                        if (column.id === "owner_name" && (!value || value.trim() === "")) {
                          return;
                        }
                        try {
                          await saveRecord(recordToSave, 'index', 'owner_index_id');
                        } catch (err) {
                          console.error("Save failed:", err);
                        }
                      },
                      handleDelete: async ({ row }) => {
                        const record = row.original;
                        const isTemp = record.new_record || String(record.owner_index_id || '').includes('temp');
                        if (window.confirm("Are you sure you want to delete this record?")) {
                          if (isTemp) {
                            const updatedData = tableData.filter(item => item.owner_index_id !== record.owner_index_id);
                            updateTableState(updatedData, 'index');
                          } else {
                            const archivedRecord = {
                              ...record,
                              archived: true,
                              archived_date: new Date().toISOString()
                            };
                            try {
                              await saveRecord(archivedRecord, 'index', 'owner_index_id');
                              const updatedData = tableData.filter(item => item.owner_index_id !== record.owner_index_id);
                              updateTableState(updatedData, 'index');
                            } catch (err) {
                              console.error("Archive failed:", err);
                              alert("Error archiving record.");
                            }
                          }
                        }
                      }
                    }}
                  />
                : renderList(data)}
          </Col>
        </Row>
      </div>
    );
  }
  return (
    <div className="OwnerPageIndexList">
      {renderComponent()}
    </div>
  );
}

export default OwnerPageIndexList;